import { useEffect, useState } from 'react';

import { scrollIntoView } from '@utils';
import { normalizeDomainLink } from '@utils/normalizeDomainLink';

/**
 * Checks if URL parts match a pattern with wildcard support
 * @param hrefParts - Array of URL path segments
 * @param patternParts - Array of pattern segments (supports '*' wildcard)
 * @returns true if the pattern matches
 */
function matchesPattern(hrefParts: string[], patternParts: string[]): boolean {
	// If pattern is longer than href, it can't match
	if (patternParts.length !== hrefParts.length) {
		return false;
	}

	for (let i = 0; i < patternParts.length; i++) {
		const patternPart = patternParts[i];
		const hrefPart = hrefParts[i];

		// Skip wildcard patterns
		if (patternPart === '*') {
			continue;
		}

		// If parts don't match exactly, pattern doesn't match
		if (patternPart !== hrefPart) {
			return false;
		}
	}

	return true;
}

export function getUrlType(href: string) {
	// @see https://www.gatsbyjs.com/docs/reference/built-in-components/gatsby-link/#reminder-use-link-only-for-internal-links
	const internal = /^\/(?!\/)/.test(href);
	const isExternalUrl =
		!internal && (href.startsWith('http') || href.startsWith('tel') || href.startsWith('mailto:'));
	const isAnchorUrl = href.includes('#');
	const isFile = /\.[0-9a-z]+$/i.test(href);

	return { isExternalUrl, isAnchorUrl, isFile };
}

export function handleAnchorLinkClick(href: string) {
	const anchorId = href.split('#')[1];
	scrollIntoView(anchorId);
}

export function getHrefPath(href: string, migrationPaths?: string[]) {
	if (!migrationPaths?.length) {
		return href;
	}

	// check if the href is only an internal
	const { isExternalUrl } = getUrlType(href);

	if (!isExternalUrl && href.startsWith('/')) {
		const hrefParts = href.split('/').filter(Boolean);
		const fullReload = migrationPaths.find((path) => matchesPattern(hrefParts, path.split('/').filter(Boolean)));
		if (fullReload) {
			return `${globalThis.location?.origin ?? ''}${href}`;
		}
	}

	return href;
}

/**
 * Custom hook to get the href path based on migration paths.
 * this is needed because SSR does not have globalThis.location.origin
 */
export function useGetHrefPath(href: string, migrationPaths?: string[]) {
	const [hrefPath, setHrefPath] = useState<string>(href);

	useEffect(() => {
		setHrefPath(getHrefPath(href, migrationPaths));
	}, [href, migrationPaths]);

	return hrefPath;
}

/**
 * Recomputes normalizeDomainLink after mount, since window.location isn't available at SSR/hydration
 * time. The initial render deliberately mirrors the untouched href (matching what the server sent)
 * so hydration never mismatches; the real, origin-aware value is applied a moment later via effect.
 */
export function useNormalizedHref(href: string | undefined, isInternal?: boolean): string | undefined {
	const [normalizedHref, setNormalizedHref] = useState<string | undefined>(href);

	useEffect(() => {
		setNormalizedHref(href ? normalizeDomainLink(href, isInternal) : href);
	}, [href, isInternal]);

	return normalizedHref;
}
